
/**
 * MotivicAnalysis.js
 * Analyzes the structural cohesion of a musical piece by tracking
 * the recurrence and variation of melodic and rhythmic motifs.
 */

export class MotivicAnalysis {

    /**
     * Analyzes the motivic integrity of a set of events.
     * @param {Array} events - Flat array of note events.
     * @param {Array} structure - Song structure sections.
     * @returns {Object} { score: number, details: Object }
     */
    static analyze(events, structure) {
        if (!events || events.length === 0) return { score: 0, details: {} };
        if (!structure || structure.length === 0) return { score: 0.5, details: { warning: "No structure" } };

        // 1. Extract Melodic Lines per Section
        const melodyEvents = events.filter(e => e.role === 'melody').sort((a,b) => a.s - b.s);
        const phrases = this.segmentBySection(melodyEvents, structure);

        if (Object.keys(phrases).length < 2) return { score: 0.5, details: { warning: "Insufficient sections" } };

        // 2. Compare Phrases (Similarity Matrix)
        let totalSimilarity = 0;
        let comparisons = 0;
        const details = {};

        // Compare Verse 1 to Verse 2 (Strict Cohesion)
        const v1 = phrases['VERSE_0'];
        const v2 = phrases['VERSE_1']; // Assuming structure labeling

        // General approach: Compare all "Like" sections
        const sectionTypes = {}; // { VERSE: [phrases], CHORUS: [phrases] }

        Object.keys(phrases).forEach(key => {
            const type = key.split('_')[0]; // VERSE, CHORUS
            if (!sectionTypes[type]) sectionTypes[type] = [];
            sectionTypes[type].push(phrases[key]);
        });

        // 2a. Internal Consistency (Repetition within Type)
        // Verse 1 vs Verse 2 should be similar
        Object.keys(sectionTypes).forEach(type => {
            const group = sectionTypes[type];
            if (group.length > 1) {
                for(let i=0; i<group.length-1; i++) {
                    const sim = this.calculatePhraseSimilarity(group[i], group[i+1]);
                    totalSimilarity += sim;
                    comparisons++;
                    details[`${type}_Consistency`] = sim;
                }
            }
        });

        // 2b. Thematic Transformation (Relationship between Verse and Chorus OR Cinematic Arc)
        // They should share *some* DNA (rhythm or contour), but not match.
        // Similarity should be > 0.3 but < 0.8
        let phraseA = sectionTypes['VERSE'] ? sectionTypes['VERSE'][0] : null;
        let phraseB = sectionTypes['CHORUS'] ? sectionTypes['CHORUS'][0] : null;

        // Cinematic Fallback: Intro -> Build -> Climax
        if (!phraseA && !phraseB) {
            const intro = sectionTypes['INTRO'] ? sectionTypes['INTRO'][0] : null;
            const build = sectionTypes['BUILD'] ? sectionTypes['BUILD'][0] : null;
            const climax = sectionTypes['CLIMAX'] ? sectionTypes['CLIMAX'][0] : null;

            if (intro && build) {
                phraseA = intro;
                phraseB = build;
            } else if (build && climax) {
                phraseA = build;
                phraseB = climax;
            } else if (intro && climax) {
                phraseA = intro;
                phraseB = climax;
            }
        }

        if (phraseA && phraseB) {
            const sim = this.calculatePhraseSimilarity(phraseA, phraseB);
            // Reward moderate similarity (Connection)
            let cohesion = 0;
            if (sim > 0.2 && sim < 0.9) cohesion = 1.0; // Widened upper bound for variations
            else cohesion = 0.5; // Too different or too same

            totalSimilarity += cohesion;
            comparisons++;
            details['Thematic_Connection'] = sim;
        }

        const finalScore = comparisons > 0 ? totalSimilarity / comparisons : 0.5;

        return {
            score: finalScore,
            details: details
        };
    }

    /**
     * Calculates similarity (0-1) between two phrases.
     * Uses Rhythm and Pitch Contour.
     */
    static calculatePhraseSimilarity(phraseA, phraseB) {
        if (!phraseA || !phraseB || phraseA.length === 0 || phraseB.length === 0) return 0;

        // 1. Rhythm String (Quantized)
        const rhythmA = this.toRhythmString(phraseA);
        const rhythmB = this.toRhythmString(phraseB);
        const rhythmSim = this.levenshteinSimilarity(rhythmA, rhythmB);

        // 2. Contour String (U, D, S)
        const contourA = this.toContourString(phraseA);
        const contourB = this.toContourString(phraseB);
        const contourSim = this.levenshteinSimilarity(contourA, contourB);

        // Weighted Average (Rhythm is stronger glue)
        return (rhythmSim * 0.6) + (contourSim * 0.4);
    }

    static segmentBySection(events, structure) {
        const result = {};
        const typeCounts = {};

        let t = 0;
        structure.forEach(s => {
            const type = s.type.toUpperCase();
            if (!typeCounts[type]) typeCounts[type] = 0;
            const key = `${type}_${typeCounts[type]}`;
            typeCounts[type]++;

            const sDur = (s.bars || 4) * 4;
            const sectionEvents = events.filter(e => e.s >= t && e.s < t + sDur);

            // Normalize time
            result[key] = sectionEvents.map(e => ({ ...e, s: e.s - t }));

            t += sDur;
        });
        return result;
    }

    static toRhythmString(phrase) {
        // Quantize to 16ths
        // "x...x.x."
        let str = "";
        if (phrase.length === 0) return "";
        const end = Math.ceil(phrase[phrase.length-1].s + (phrase[phrase.length-1].len || 0.25));
        const grid = new Array(end * 4).fill('.');

        phrase.forEach(e => {
            const pos = Math.round(e.s * 4);
            if (pos < grid.length) grid[pos] = 'x';
        });
        return grid.join('');
    }

    static toContourString(phrase) {
        // "U" (Up), "D" (Down), "S" (Same)
        if (phrase.length < 2) return "";
        let str = "";
        for(let i=1; i<phrase.length; i++) {
            const diff = phrase[i].d - phrase[i-1].d;
            if (diff > 0) str += "U";
            else if (diff < 0) str += "D";
            else str += "S";
        }
        return str;
    }

    static levenshteinSimilarity(a, b) {
        if (a.length === 0) return b.length === 0 ? 1.0 : 0.0;
        if (b.length === 0) return 0.0;

        const matrix = [];

        // Increment along the first column of each row
        for (let i = 0; i <= b.length; i++) {
            matrix[i] = [i];
        }

        // Increment each column in the first row
        for (let j = 0; j <= a.length; j++) {
            matrix[0][j] = j;
        }

        // Fill in the rest of the matrix
        for (let i = 1; i <= b.length; i++) {
            for (let j = 1; j <= a.length; j++) {
                if (b.charAt(i - 1) === a.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1, // substitution
                        Math.min(
                            matrix[i][j - 1] + 1, // insertion
                            matrix[i - 1][j] + 1  // deletion
                        )
                    );
                }
            }
        }

        const distance = matrix[b.length][a.length];
        const maxLen = Math.max(a.length, b.length);
        return 1.0 - (distance / maxLen);
    }
}
